// 채점 근거 하이라이트: 백엔드가 계산한 anchor(0~1 정규화 좌표)를 페이지 PNG 위에 판정 색으로 겹쳐 보여준다.
// - 항상 표시(토글 없음). anchor가 없으면 호출 측이 기존 미리보기를 그대로 보여준다.
// - 색: 충족 초록 / 일부·확인 주황 / 부족 빨강. 색맹 대비로 번호 + 기호(✓ △ ✕)를 함께 표기한다.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchScoringEvidencePageImage, getScoringEvidenceAnchors, requestEvidenceAnchorsInBackground } from '../../api/scoringApi';

export const JUDGMENT_SYMBOL = { met: '✓', partial: '△', missing: '✕' };
export const JUDGMENT_LABEL = { met: '충족', partial: '일부', missing: '부족' };
const JUDGMENT_Z_ORDER = { met: 1, partial: 2, missing: 3 };

const ANCHOR_POLL_INTERVAL_MS = 4000;
const ANCHOR_POLL_MIN_MS = 4 * 60 * 1000;
// 서버 계산 요청 타임아웃 상한(computeScoringEvidenceAnchors 15분)과 맞춘다.
const ANCHOR_POLL_CAP_MS = 15 * 60 * 1000;
const ANCHOR_POLL_PER_RESULT_MS = 15 * 1000;
const ANCHOR_POLL_MAX_CONSECUTIVE_ERRORS = 3;

/** 학생 수가 많으면 계산도 오래 걸리므로 폴링 시간을 늘린다. */
export const anchorPollMaxMs = (expectedCount) => (
  Math.min(ANCHOR_POLL_CAP_MS, Math.max(ANCHOR_POLL_MIN_MS, (Number(expectedCount) || 0) * ANCHOR_POLL_PER_RESULT_MS))
);

export const normalizeQuoteText = (value) => String(value || '').replace(/[\s「」『』"'“”‘’.,!?~·()[\]\-–—:;/]/g, '');

export const circledNumber = (n) => (n >= 1 && n <= 20 ? String.fromCharCode(0x2460 + n - 1) : String(n));

const isDocSettled = (doc) => doc && ['done', 'skipped', 'failed'].includes(doc.status);

/** 다른 판정의 더 큰 하이라이트 안에 놓인 부분 구절(백엔드 layer="inner"). 형광펜 대신 굵은 밑줄로 위에 그린다. */
export const isInnerAnchor = (anchor) => (anchor?.layer === 'inner' ? 1 : 0);
const anchorArea = (anchor) => (Array.isArray(anchor?.segments)
  ? anchor.segments.reduce((sum, s) => sum + (Number(s.w) || 0) * (Number(s.h) || 0), 0)
  : 0);

const ANCHOR_REFRESH_POLL_MAX_MS = 45 * 1000;

/** 기록의 anchor 문서를 불러오고, 아직 계산 중이면 잠시 폴링한다. { byIndex, status }

 * refreshKey: 결과의 인용문·점수가 바뀌었을 때(AI 수정, 점수 수정) 달라지는 값. 같은 기록에서 바뀌면
 * 기존 하이라이트는 유지한 채 다시 폴링해 갱신된 문서(updated_at 변경)를 받는다.
 */
export function useScoringEvidenceAnchors(recordId, { expectedCount = 0, refreshKey = '' } = {}) {
  const [state, setState] = useState({ byIndex: {}, status: 'idle' });
  const lastRecordIdRef = useRef(null);
  const byIndexRef = useRef({});
  byIndexRef.current = state.byIndex;
  const staleRequestedRef = useRef(false);

  useEffect(() => {
    if (!recordId) {
      lastRecordIdRef.current = null;
      setState({ byIndex: {}, status: 'idle' });
      return undefined;
    }
    const isRefresh = lastRecordIdRef.current === recordId;
    lastRecordIdRef.current = recordId;
    const previousUpdatedAt = isRefresh
      ? Object.fromEntries(Object.entries(byIndexRef.current).map(([k, d]) => [k, d?.updated_at ?? null]))
      : null;
    let cancelled = false;
    let timer = null;
    let consecutiveErrors = 0;
    const startedAt = Date.now();
    const pollMaxMs = anchorPollMaxMs(expectedCount);
    staleRequestedRef.current = false;

    const tick = async () => {
      try {
        const response = await getScoringEvidenceAnchors(recordId);
        if (cancelled) return;
        consecutiveErrors = 0;
        if (response?.enabled === false) {
          setState({ byIndex: {}, status: 'disabled' });
          return;
        }
        const byIndex = response?.anchors || {};
        setState({ byIndex, status: 'ready' });
        const docs = Object.values(byIndex);
        // 서버가 stale(인용문·판정·스키마 버전이 바뀐 뒤 미계산)로 표시한 결과는 한 번만 재계산을 요청하고
        // 갱신될 때까지 폴링한다. Vision 재호출 없이 OCR 캐시로 재매칭되므로 수 초 안에 끝난다.
        const staleIndexes = Object.entries(byIndex).filter(([, d]) => d?.stale === true).map(([k]) => Number(k));
        if (staleIndexes.length > 0 && !staleRequestedRef.current) {
          staleRequestedRef.current = true;
          requestEvidenceAnchorsInBackground(recordId, staleIndexes);
        }
        let settled = docs.length >= Math.max(1, expectedCount) && docs.every(isDocSettled)
          && (staleIndexes.length === 0 || Date.now() - startedAt >= ANCHOR_REFRESH_POLL_MAX_MS);
        if (settled && previousUpdatedAt) {
          // 갱신 요청 직후에는 아직 이전 문서일 수 있다 → 어느 하나라도 updated_at이 바뀌면 끝.
          const changed = Object.entries(byIndex).some(([k, d]) => (d?.updated_at ?? null) !== (previousUpdatedAt[k] ?? null));
          if (!changed && Date.now() - startedAt < ANCHOR_REFRESH_POLL_MAX_MS) settled = false;
        }
        if (!settled && Date.now() - startedAt < pollMaxMs) {
          timer = setTimeout(tick, ANCHOR_POLL_INTERVAL_MS);
        }
      } catch (error) {
        if (cancelled) return;
        consecutiveErrors += 1;
        // 일시 오류(네트워크·배포 중 5xx) 한 번으로 폴링이 끝나면 계산이 끝나도 하이라이트가 안 뜬다.
        const willRetry = consecutiveErrors < ANCHOR_POLL_MAX_CONSECUTIVE_ERRORS && Date.now() - startedAt < pollMaxMs;
        if (willRetry) {
          timer = setTimeout(tick, ANCHOR_POLL_INTERVAL_MS * (consecutiveErrors + 1));
        } else {
          setState((prev) => ({ ...prev, status: prev.status === 'ready' ? 'ready' : 'error' }));
        }
      }
    };
    // 같은 기록의 갱신(refreshKey 변경)이면 기존 문서를 그대로 보여주며 조용히 다시 받는다(loading 으로 되돌리지 않음)
    setState((prev) => ({ ...prev, status: isRefresh && prev.status === 'ready' ? 'ready' : 'loading' }));
    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [recordId, expectedCount, refreshKey]);

  return state;
}

/** 위치가 확인된 anchor만 페이지·세로 순으로 정렬해 번호를 매긴다. */
export function buildLocatedAnchors(doc) {
  const anchors = Array.isArray(doc?.anchors) ? doc.anchors : [];
  return anchors
    .filter((a) => a && a.page !== null && a.page !== undefined && Array.isArray(a.segments) && a.segments.length > 0
      && (a.status === 'confirmed' || a.status === 'approx'))
    .sort((a, b) => (a.page - b.page) || (a.segments[0].y - b.segments[0].y) || (a.segments[0].x - b.segments[0].x))
    .map((a, index) => ({ ...a, number: index + 1 }));
}

/** 오른쪽 패널의 인용문(칩)에 해당하는 anchor를 찾는다. 인덱스 + 정규화 텍스트로 매칭.
 * 백엔드가 같은 구절을 하나로 병합한 anchor는 refs[]에 원래 참조를 모두 담고 있으므로 refs도 본다. */
export function findAnchorForQuote(located, { source, itemIndex, checkIndex = null, questionIndex = null, quote }) {
  if (!Array.isArray(located) || located.length === 0) return null;
  const normalized = normalizeQuoteText(quote);
  if (!normalized) return null;
  const sameScope = (r) => (
    r.item_index === itemIndex
    && ((r.question_index ?? null) === (questionIndex ?? null))
  );
  const refsOf = (a) => (Array.isArray(a.refs) && a.refs.length > 0 ? a.refs : [a]);
  const exact = located.find((a) => refsOf(a).some((r) => r.source === source && sameScope(r)
    && (r.check_index ?? null) === (checkIndex ?? null) && normalizeQuoteText(r.quote) === normalized));
  if (exact) return exact;
  return located.find((a) => refsOf(a).some((r) => sameScope(r) && normalizeQuoteText(r.quote) === normalized)) || null;
}

function ScoringEvidencePage({
  recordId,
  resultIndex,
  page,
  anchors,
  activeKey,
  onSelect,
  registerSegmentRef,
  onImageError,
}) {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);
  const pageRef = useRef(null);
  const onImageErrorRef = useRef(onImageError);
  onImageErrorRef.current = onImageError;

  useEffect(() => {
    let cancelled = false;
    let objectUrl = '';
    setSrc('');
    setFailed(false);
    // 앵커 계산 때 Storage에 올려둔 이미지의 서명 URL이 있으면 그대로 쓴다(빠름, 브라우저 캐시 가능).
    // 없으면(계산 전·업로드 실패) 인증이 필요한 렌더 엔드포인트로 받는다.
    if (page.image_url) {
      setSrc(page.image_url);
      return undefined;
    }
    fetchScoringEvidencePageImage(recordId, resultIndex, page.index)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        objectUrl = url;
        setSrc(url);
      })
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
        onImageErrorRef.current?.(page.index);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [recordId, resultIndex, page.index, page.image_url]);

  const aspectRatio = page.width && page.height ? `${page.width} / ${page.height}` : '1 / 1.4142';
  // 그리는 순서: base(형광펜) 먼저·넓은 것부터, inner(다른 판정의 부분 구절, 밑줄) 는 항상 맨 위.
  const ordered = useMemo(() => (
    [...anchors].sort((a, b) => (
      (isInnerAnchor(a) - isInnerAnchor(b))
      || (anchorArea(b) - anchorArea(a))
      || ((JUDGMENT_Z_ORDER[a.judgment] || 0) - (JUDGMENT_Z_ORDER[b.judgment] || 0))
    ))
  ), [anchors]);
  const pageHeightPx = useElementHeight(pageRef, src);
  const markers = useMemo(() => layoutGutterMarkers(anchors, pageHeightPx), [anchors, pageHeightPx]);

  if (failed) return null;

  const labelOf = (anchor) => (
    `${anchor.number}번 근거, ${JUDGMENT_LABEL[anchor.judgment] || '확인'}`
    + `${anchor.status === 'approx' ? ', 근사 위치' : ''}${isInnerAnchor(anchor) ? ', 부분 구절' : ''}`
  );

  return (
    <div className="scoring-evidence-page-row">
      {/* 여백 마커: 번호표를 글씨 위가 아니라 왼쪽 여백에, 줄 높이에 맞춰 세로로 정렬한다(겹치면 아래로 밀어냄). */}
      <div className="scoring-evidence-gutter">
        {src && markers.map(({ anchor, top }) => {
          const isActive = activeKey === anchor.quote_key;
          return (
            <button
              key={anchor.quote_key}
              type="button"
              className={[
                'scoring-evidence-marker',
                `is-${anchor.judgment || 'met'}`,
                anchor.status === 'approx' ? 'is-approx' : '',
                isActive ? 'is-active' : '',
              ].filter(Boolean).join(' ')}
              style={{ top: typeof top === 'number' ? `${top}px` : top }}
              aria-label={`${anchor.number}번 마커, ${JUDGMENT_LABEL[anchor.judgment] || '확인'}`}
              aria-pressed={isActive}
              title={anchor.quote}
              onClick={() => onSelect?.(anchor.quote_key)}
              data-testid="scoring-evidence-marker"
            >
              {circledNumber(anchor.number)}
              <em aria-hidden="true">{JUDGMENT_SYMBOL[anchor.judgment] || ''}</em>
            </button>
          );
        })}
      </div>
      <div ref={pageRef} className={`scoring-evidence-page ${src ? 'is-loaded' : 'is-loading'}`} style={{ aspectRatio }}>
        {src ? (
          <img
            src={src}
            alt={`제출물 ${page.index + 1}쪽`}
            className="scoring-evidence-page-image"
            draggable={false}
            decoding="async"
            loading={page.index <= 1 ? 'eager' : 'lazy'}
            // React 18은 camelCase를 모르므로 소문자 DOM 속성으로 전달
            fetchpriority={page.index === 0 ? 'high' : 'auto'}
          />
        ) : (
          <div className="scoring-evidence-page-skeleton" aria-hidden="true" />
        )}
        {src && ordered.map((anchor) => anchor.segments.map((seg, segIndex) => {
          const isActive = activeKey === anchor.quote_key;
          return (
            <button
              key={`${anchor.quote_key}-${segIndex}`}
              type="button"
              ref={segIndex === 0 ? (node) => registerSegmentRef?.(anchor.quote_key, node) : undefined}
              className={[
                'scoring-evidence-seg',
                `is-${anchor.judgment || 'met'}`,
                anchor.status === 'approx' ? 'is-approx' : '',
                isInnerAnchor(anchor) ? 'is-inner' : '',
                isActive ? 'is-active' : '',
              ].filter(Boolean).join(' ')}
              style={{
                left: `${seg.x * 100}%`,
                top: `${seg.y * 100}%`,
                width: `${seg.w * 100}%`,
                height: `${seg.h * 100}%`,
              }}
              aria-label={labelOf(anchor)}
              aria-pressed={isActive}
              title={anchor.quote}
              onClick={() => onSelect?.(anchor.quote_key)}
            />
          );
        }))}
      </div>
    </div>
  );
}

/** 이웃 학생(이전·다음)의 첫 페이지 이미지를 미리 받아 두어 학생 전환이 즉시 되게 한다. 서명 URL 은
 * 시간대 단위로 고정되어 브라우저 캐시에 그대로 맞는다. 반환값: 프리페치한 URL 목록(테스트용). */
const prefetchedUrls = new Set();
export function prefetchNeighborPageImages(byIndex, selectedIndex, count) {
  if (typeof Image === 'undefined' || !byIndex) return [];
  const urls = [];
  [selectedIndex - 1, selectedIndex + 1].forEach((idx) => {
    if (idx < 0 || idx >= count) return;
    const doc = byIndex[String(idx)];
    const pages = Array.isArray(doc?.pages) ? [...doc.pages].sort((a, b) => a.index - b.index) : [];
    const url = pages[0]?.image_url;
    if (!url || prefetchedUrls.has(url)) return;
    prefetchedUrls.add(url);
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    urls.push(url);
  });
  return urls;
}

export const GUTTER_MARKER_HEIGHT_PX = 20;
const GUTTER_MARKER_GAP_PX = 3;

/** 여백 마커의 세로 위치. 첫 세그먼트의 줄 중앙에 맞추고, 앞 마커와 겹치면 아래로 밀어낸다.
 * 페이지 픽셀 높이를 모르면(측정 전) 퍼센트 위치만 반환한다. */
export function layoutGutterMarkers(anchors, pageHeightPx) {
  const sorted = [...anchors]
    .filter((a) => Array.isArray(a.segments) && a.segments.length > 0)
    .sort((a, b) => (a.segments[0].y - b.segments[0].y) || (a.segments[0].x - b.segments[0].x));
  if (!pageHeightPx || pageHeightPx <= 0) {
    return sorted.map((anchor) => ({ anchor, top: `calc(${(anchor.segments[0].y + anchor.segments[0].h / 2) * 100}% - ${GUTTER_MARKER_HEIGHT_PX / 2}px)` }));
  }
  const step = GUTTER_MARKER_HEIGHT_PX + GUTTER_MARKER_GAP_PX;
  const maxTop = Math.max(0, pageHeightPx - GUTTER_MARKER_HEIGHT_PX);
  const tops = [];
  sorted.forEach((anchor, i) => {
    const seg = anchor.segments[0];
    const desired = (seg.y + seg.h / 2) * pageHeightPx - GUTTER_MARKER_HEIGHT_PX / 2;
    const top = i === 0 ? desired : Math.max(desired, tops[i - 1] + step);
    tops.push(Math.max(0, top));
  });
  // 아래로 넘치면 위로 되밀기(마지막부터)
  for (let i = tops.length - 1; i >= 0; i -= 1) {
    const limit = i === tops.length - 1 ? maxTop : tops[i + 1] - step;
    if (tops[i] > limit) tops[i] = Math.max(0, limit);
  }
  return sorted.map((anchor, i) => ({ anchor, top: Math.round(tops[i]) }));
}

function useElementHeight(ref, ready) {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const measure = () => setHeight(node.getBoundingClientRect().height || node.offsetHeight || 0);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, ready]);
  return height;
}

/** 결과 하나의 모든 렌더 페이지를 세로로 쌓고 anchor를 겹친다. */
export function ScoringEvidencePages({
  recordId,
  resultIndex,
  doc,
  located,
  activeKey,
  onSelect,
  registerSegmentRef,
  onUnavailable,
}) {
  const pages = useMemo(() => (
    [...(Array.isArray(doc?.pages) ? doc.pages : [])].sort((a, b) => a.index - b.index)
  ), [doc]);
  const failedPagesRef = useRef(new Set());
  const pageCount = pages.length;

  const handleImageError = useCallback((pageIndex) => {
    failedPagesRef.current.add(pageIndex);
    if (failedPagesRef.current.size >= pageCount) {
      onUnavailable?.();
    }
  }, [pageCount, onUnavailable]);

  if (pages.length === 0) return null;

  return (
    <div className="scoring-evidence-pages" data-testid="scoring-evidence-pages">
      {pages.map((page) => (
        <ScoringEvidencePage
          key={page.index}
          recordId={recordId}
          resultIndex={resultIndex}
          page={page}
          anchors={located.filter((a) => a.page === page.index)}
          activeKey={activeKey}
          onSelect={onSelect}
          registerSegmentRef={registerSegmentRef}
          onImageError={handleImageError}
        />
      ))}
    </div>
  );
}

/** 칩에 표시할 때 앞뒤 인용 괄호(「」『』"")를 벗긴다. 번호표가 인용임을 이미 나타내므로 시각 밀도를 낮춘다. */
export const stripQuoteBrackets = (value) => String(value || '').trim().replace(/^[「『"“]+/, '').replace(/[」』"”]+$/, '').trim();

/**
 * AI 근거 본문을 「…」 인용 단위로 나눈다. resolveAnchor(quote)가 anchor를 돌려주면 그 조각은 클릭 가능한
 * 인라인 링크로 그린다. [{ text, anchor|null }]
 */
export function splitReasonByQuotes(reason, resolveAnchor) {
  const text = String(reason || '');
  if (!text) return [];
  const parts = [];
  const re = /「([^「」]{4,200})」/g;
  let last = 0;
  let match = re.exec(text);
  while (match) {
    const anchor = typeof resolveAnchor === 'function' ? resolveAnchor(match[1]) : null;
    if (anchor) {
      if (match.index > last) parts.push({ text: text.slice(last, match.index), anchor: null });
      parts.push({ text: match[1], anchor });
      last = match.index + match[0].length;
    }
    match = re.exec(text);
  }
  if (last < text.length) parts.push({ text: text.slice(last), anchor: null });
  return parts;
}

/** AI 근거 본문 안의 인용을 하이라이트로 연결하는 인라인 링크(번호 + 밑줄).
 * <button>은 브라우저가 inline-block으로 강제해 문장 중간에서 줄바꿈이 안 되므로 role=button인 span을 쓴다. */
export function ScoringEvidenceInlineQuote({ anchor, activeKey, onSelect, registerChipRef, children }) {
  const isActive = activeKey === anchor.quote_key;
  const select = () => onSelect?.(anchor.quote_key);
  return (
    <span
      role="button"
      tabIndex={0}
      ref={(node) => registerChipRef?.(anchor.quote_key, node)}
      className={[
        'scoring-evidence-inline',
        `is-${anchor.judgment || 'met'}`,
        isActive ? 'is-active' : '',
      ].filter(Boolean).join(' ')}
      aria-pressed={isActive}
      title={`${anchor.number}번 근거 위치 보기`}
      onClick={select}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          select();
        }
      }}
    >
      <span className="scoring-evidence-inline-tag">{circledNumber(anchor.number)}</span>
      <span className="scoring-evidence-inline-text">{children}</span>
    </span>
  );
}

/** 오른쪽 패널의 인용문 칩. anchor가 있을 때만 버튼(번호+기호)으로 그려진다. */
export function ScoringEvidenceQuoteChip({ anchor, activeKey, onSelect, registerChipRef, children, className = '' }) {
  if (!anchor) {
    return <p className={className}>{children}</p>;
  }
  const isActive = activeKey === anchor.quote_key;
  return (
    <button
      type="button"
      ref={(node) => registerChipRef?.(anchor.quote_key, node)}
      className={[
        className,
        'scoring-evidence-chip',
        `is-${anchor.judgment || 'met'}`,
        isActive ? 'is-active' : '',
      ].filter(Boolean).join(' ')}
      aria-pressed={isActive}
      onClick={() => onSelect?.(anchor.quote_key)}
    >
      <span className="scoring-evidence-chip-tag">
        {circledNumber(anchor.number)}
        <em aria-label={JUDGMENT_LABEL[anchor.judgment] || ''}>{JUDGMENT_SYMBOL[anchor.judgment] || ''}</em>
      </span>
      <span className="scoring-evidence-chip-text">{children}</span>
    </button>
  );
}
